import React, { useEffect, useState } from "react";

// Shared Tutoring inbox UI. Used by both HQ admin and the branch sub-app —
// they pass different `api` impls (different endpoints + scoping).
//
// `api` shape:
//   listThreads({status, branch_id, search}) → { status, data:[] }
//   getThread(id)                            → { status, data:{...,replies:[]} }
//   reply({ thread_id, body, attachment_url, ...extra }) → { status }
//   setStatus(id, status, extra?)            → { status }
//   uploadAttachment(file)                   → { status, data:{ url } }
//
// `extra` from caller, e.g. branch passes { branch_id, user_id }; HQ passes
// { user_id }. The component just forwards.

const STATUS_PILL = {
  open: { bg: "#fef3c7", fg: "#92400e", label: "Open" },
  answered: { bg: "#dcfce7", fg: "#166534", label: "Answered" },
  closed: { bg: "#e5e7eb", fg: "#4b5563", label: "Closed" },
};

const fmtWhen = (s) => {
  if (!s) return "";
  const d = new Date(s);
  return d.toLocaleString("en-AU", {
    month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit",
  });
};

const TutoringInbox = ({ api, scope }) => {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: "", search: "" });
  const [activeId, setActiveId] = useState(null);
  const [activeThread, setActiveThread] = useState(null);

  const load = async () => {
    setLoading(true);
    const res = await api.listThreads({
      status: filter.status || undefined,
      search: filter.search || undefined,
    });
    setThreads(res?.data || []);
    setLoading(false);
  };

  const loadThread = async (id) => {
    setActiveId(id);
    setActiveThread(null);
    const res = await api.getThread(id);
    if (res?.status) setActiveThread(res.data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  return (
    <div className="right-nav" id="dash-event" style={{ padding: "20px 24px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, gap: 12, flexWrap: "wrap" }}>
        <div>
          <h4 style={{ fontSize: 18, fontWeight: 600, margin: 0, color: "#1a1a2e" }}>Tutoring</h4>
          <p style={{ fontSize: 12, color: "#64748b", margin: "4px 0 0" }}>
            {scope === "branch"
              ? "Questions from students in your branch."
              : "All student questions across branches."}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <select
            value={filter.status}
            onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value }))}
            style={{
              fontSize: 12, padding: "6px 10px", borderRadius: 6,
              border: "1px solid #d1d5db", background: "#fff",
            }}
          >
            <option value="">All status</option>
            <option value="open">Open</option>
            <option value="answered">Answered</option>
            <option value="closed">Closed</option>
          </select>
          <input
            type="search"
            placeholder="Search…"
            value={filter.search}
            onChange={(e) => setFilter((f) => ({ ...f, search: e.target.value }))}
            style={{
              fontSize: 12, padding: "6px 10px", borderRadius: 6,
              border: "1px solid #d1d5db", minWidth: 220,
            }}
          />
        </div>
      </div>

      <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
        <div style={{
          display: "grid",
          gridTemplateColumns: "1.4fr 1fr 0.6fr 0.6fr 110px 90px",
          padding: "10px 14px", background: "#f9fafb",
          fontSize: 11, color: "#6b7280", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5,
        }}>
          <span>Subject</span>
          <span>Student</span>
          <span>Branch</span>
          <span>Last activity</span>
          <span>Status</span>
          <span style={{ textAlign: "right" }}>Replies</span>
        </div>
        {loading ? (
          <div style={{ padding: 20, color: "#94a3b8", fontSize: 13 }}>Loading…</div>
        ) : threads.length === 0 ? (
          <div style={{ padding: 24, color: "#94a3b8", fontSize: 13 }}>No questions yet.</div>
        ) : threads.map((t) => {
          const s = STATUS_PILL[t.status] || STATUS_PILL.open;
          return (
            <button key={t.id} type="button" onClick={() => loadThread(t.id)} style={{
              display: "grid", gridTemplateColumns: "1.4fr 1fr 0.6fr 0.6fr 110px 90px",
              padding: "12px 14px", borderTop: "1px solid #f1f5f9", alignItems: "center",
              fontSize: 13, color: "#334155", textAlign: "left",
              background: "transparent", border: "none", borderTop: "1px solid #f1f5f9",
              width: "100%", cursor: "pointer",
            }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#f9fafb")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              <span style={{ fontWeight: 500, color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {t.title}
              </span>
              <span style={{ fontSize: 12 }}>
                {t.student_first_name || ""} {t.student_last_name || ""}
                {t.grade_name ? <span style={{ color: "#94a3b8" }}> · Y{t.grade_name}</span> : null}
              </span>
              <span style={{ fontSize: 12, color: "#64748b" }}>{t.branch_name || "—"}</span>
              <span style={{ fontSize: 12, color: "#64748b" }}>{fmtWhen(t.last_reply_at || t.create_at)}</span>
              <span>
                <span style={{
                  fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 999,
                  background: s.bg, color: s.fg, textTransform: "uppercase",
                }}>
                  {s.label}
                </span>
              </span>
              <span style={{ textAlign: "right", fontSize: 12, color: "#94a3b8" }}>
                {t.reply_count || 0}
              </span>
            </button>
          );
        })}
      </div>

      {activeId && (
        <ThreadModal
          api={api}
          thread={activeThread}
          onClose={() => { setActiveId(null); setActiveThread(null); }}
          onChanged={async () => { await loadThread(activeId); await load(); }}
        />
      )}
    </div>
  );
};

const ThreadModal = ({ api, thread, onClose, onChanged }) => {
  const [reply, setReply] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!thread) return;
    if (!reply.trim() && !file) return;
    setBusy(true);
    let attachment_url = null;
    if (file) {
      const up = await api.uploadAttachment(file);
      if (up?.status) attachment_url = up.data?.url;
      else { setBusy(false); return; }
    }
    const res = await api.reply({ thread_id: thread.id, body: reply, attachment_url });
    setBusy(false);
    if (res?.status) {
      setReply("");
      setFile(null);
      onChanged();
    }
  };

  const setStatus = async (status) => {
    if (busy) return;
    setBusy(true);
    await api.setStatus(thread.id, status);
    setBusy(false);
    onChanged();
  };

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 1050, padding: 24, overflowY: "auto",
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "#fff", borderRadius: 10, maxWidth: 720, width: "100%",
        padding: "20px 24px", boxShadow: "0 20px 50px rgba(15,23,42,0.25)",
        maxHeight: "calc(100vh - 48px)", overflowY: "auto",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12, gap: 12 }}>
          <div>
            <h5 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: "#1f2937" }}>
              {thread?.title || "Loading…"}
            </h5>
            {thread && (
              <p style={{ margin: "4px 0 0", fontSize: 12, color: "#64748b" }}>
                {thread.student_first_name} {thread.student_last_name}
                {thread.grade_name ? ` · Y${thread.grade_name}` : ""}
                {thread.branch_name ? ` · ${thread.branch_name}` : ""}
              </p>
            )}
          </div>
          <button onClick={onClose} type="button"
            style={{ background: "transparent", border: "none", fontSize: 22, color: "#94a3b8", cursor: "pointer", padding: 0 }}>×</button>
        </div>

        {!thread ? (
          <p style={{ color: "#94a3b8", fontSize: 13 }}>Loading…</p>
        ) : (
          <>
            {thread.body && (
              <Bubble role="student" name={`${thread.student_first_name} ${thread.student_last_name}`} body={thread.body} when={thread.create_at} />
            )}
            {thread.replies?.map((r) => (
              <Bubble key={r.id}
                role={r.role}
                name={r.role === "student" ? `${r.first_name || "Student"}` : (r.first_name || (r.role === "branch" ? "Branch" : "CS Education"))}
                body={r.body}
                attachment={r.attachment_url}
                when={r.create_at}
              />
            ))}

            <div style={{ display: "flex", gap: 8, marginTop: 14, marginBottom: 12, flexWrap: "wrap" }}>
              {thread.status !== "answered" && (
                <button type="button" onClick={() => setStatus("answered")}
                  style={chipBtn("#dcfce7", "#166534")} disabled={busy}>
                  Mark answered
                </button>
              )}
              {thread.status !== "closed" && (
                <button type="button" onClick={() => setStatus("closed")}
                  style={chipBtn("#e5e7eb", "#4b5563")} disabled={busy}>
                  Close thread
                </button>
              )}
              {thread.status === "closed" && (
                <button type="button" onClick={() => setStatus("open")}
                  style={chipBtn("#fef3c7", "#92400e")} disabled={busy}>
                  Reopen
                </button>
              )}
            </div>

            {thread.status !== "closed" && (
              <form onSubmit={submit} style={{ borderTop: "1px solid #f1f5f9", paddingTop: 12 }}>
                <textarea className="form-control" rows="3" placeholder="Reply as staff…"
                  value={reply} onChange={(e) => setReply(e.target.value)} disabled={busy}></textarea>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
                  <input type="file" accept="image/*,application/pdf"
                    onChange={(e) => setFile(e.target.files?.[0] || null)} disabled={busy}
                    style={{ fontSize: 12 }} />
                  <button type="submit" className="btn btn-primary"
                    disabled={busy || (!reply.trim() && !file)}>Send reply</button>
                </div>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const chipBtn = (bg, fg) => ({
  fontSize: 11, fontWeight: 600, padding: "5px 12px", borderRadius: 999,
  background: bg, color: fg, border: "none", cursor: "pointer",
});

const Bubble = ({ role, name, body, attachment, when }) => {
  const isStudent = role === "student";
  return (
    <div style={{ display: "flex", justifyContent: isStudent ? "flex-start" : "flex-end", marginBottom: 12 }}>
      <div style={{
        maxWidth: "78%",
        background: isStudent ? "#f1f5f9" : "#dbeafe",
        color: "#1f2937",
        borderRadius: 10, padding: "10px 14px", fontSize: 13, lineHeight: 1.5,
      }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: isStudent ? "#475569" : "#1e40af", marginBottom: 4 }}>
          {name}
          <span style={{ fontWeight: 400, marginLeft: 8, color: "#94a3b8" }}>{fmtWhen(when)}</span>
        </div>
        {body && <div style={{ whiteSpace: "pre-wrap" }}>{body}</div>}
        {attachment && (
          <a href={attachment} target="_blank" rel="noopener noreferrer"
             style={{ display: "inline-block", marginTop: body ? 8 : 0, fontSize: 12, color: "#2563eb" }}>
            <i className="fa fa-paperclip" style={{ marginRight: 4 }}></i> Attachment
          </a>
        )}
      </div>
    </div>
  );
};

export default TutoringInbox;
